/* global window */

import * as Sentry from "@sentry/browser";
import { API_CONFIG } from "../config/api.js";
import { keepBreadcrumb, pseudonymousUserId, redactEvent } from "./sentryPrivacy.js";

const SENSITIVE_KEYWORDS = [
  "authorization",
  "cookie",
  "set-cookie",
  "password",
  "secret",
  "token",
  "api_key",
  "x-api-key",
  "csrf",
  "device_code",
  "user_code",
  "verifier",
  "challenge",
];

const CREDENTIAL_PATTERNS = [
  [/\bBearer\s+[A-Za-z0-9._~-]+/gi, "[REDACTED]"],
  [/\b(?:sk|ws|dc)-[A-Za-z0-9_-]{16,}\b/g, "[REDACTED]"],
  [/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, "[REDACTED]"],
  [/\bnovatime_session=[^;\s]+/gi, "[REDACTED]"],
  [/(authorization|x-api-key|api_key|access_token|refresh_token|csrf_token|device_code|user_code|device_secret|verifier|cookie)(\s*[:=]\s*)([^,;\s}]+)/gi, "$1$2[REDACTED]"],
];

let sentryInitialized = false;
let sentryEnabled = false;
let sentryReady = null;
// Scope set before Sentry finished loading its settings, applied once it has.
let pendingTags = {};
let pendingContexts = {};
let pendingUserId;
// GID-83: the chat in view and the Langfuse trace of its newest reply. Kept
// here rather than as scope tags so it can be cleared (setTag cannot unset)
// and so it is right even for events sent before Sentry finished loading.
let chatContext = { sessionId: null, traceId: null };

function isSensitiveKey(key) {
  if (typeof key !== "string") return false;
  const normalized = key.trim().toLowerCase().replace(/-/g, "_");
  return SENSITIVE_KEYWORDS.some((word) => normalized.includes(word));
}

function scrubSensitiveText(value) {
  return CREDENTIAL_PATTERNS.reduce(
    (result, [pattern, replacement]) => result.replace(pattern, replacement),
    value
  );
}

function scrubSensitiveData(value) {
  if (Array.isArray(value)) {
    return value.map((item) => scrubSensitiveData(item));
  }
  if (value && typeof value === "object") {
    const output = {};
    for (const [key, nested] of Object.entries(value)) {
      output[key] = isSensitiveKey(key) ? "[REDACTED]" : scrubSensitiveData(nested);
    }
    return output;
  }
  if (typeof value === "string") return scrubSensitiveText(value);
  return value;
}

function coerceSampleRate(value, defaultValue) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return defaultValue;
  if (parsed < 0) return 0.0;
  if (parsed > 1) return 1.0;
  return parsed;
}

function parseList(value) {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function tryGetOrigin(url) {
  try {
    return new URL(url).origin;
  } catch (_) {
    return "";
  }
}

function resolveRelease() {
  return (
    process.env.SENTRY_RELEASE ||
    process.env.GIT_COMMIT ||
    process.env.COMMIT_SHA ||
    process.env.CI_COMMIT_SHA ||
    "frontend-dev"
  );
}

function resolveTracePropagationTargets() {
  const configuredTargets = parseList(process.env.SENTRY_TRACE_PROPAGATION_TARGETS);
  const apiBaseUrl = API_CONFIG?.baseURL || "";
  const apiOrigin = tryGetOrigin(apiBaseUrl);
  const windowOrigin = typeof window !== "undefined" && window.location ? window.location.origin : "";

  const targets = new Set(["/api/"]);
  if (apiBaseUrl) targets.add(apiBaseUrl);
  if (apiOrigin) targets.add(apiOrigin);
  if (windowOrigin) targets.add(windowOrigin);
  for (const target of configuredTargets) targets.add(target);

  return Array.from(targets);
}

function setScopeValues(scope, options = {}) {
  // No per-event user: setSentryUser keeps it pseudonymous for the session.
  const { tags, contexts, extra, fingerprint, level } = options;
  if (tags) {
    Object.entries(tags).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim()) {
        scope.setTag(key, String(value));
      }
    });
  }
  if (contexts) {
    Object.entries(contexts).forEach(([key, value]) => {
      scope.setContext(String(key), scrubSensitiveData(value));
    });
  }
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      scope.setExtra(String(key), scrubSensitiveData(value));
    });
  }
  if (Array.isArray(fingerprint) && fingerprint.length > 0) {
    scope.setFingerprint(fingerprint.map((item) => String(item)));
  }
  if (level) {
    scope.setLevel(level);
  }
}

function stringHash(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return (hash >>> 0).toString(16);
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, null, 0);
  } catch (_) {
    return String(value);
  }
}

function toUtf8Bytes(value) {
  if (typeof TextEncoder !== "undefined") {
    return new TextEncoder().encode(value);
  }
  const encoded = unescape(encodeURIComponent(value));
  const bytes = new Uint8Array(encoded.length);
  for (let i = 0; i < encoded.length; i += 1) {
    bytes[i] = encoded.charCodeAt(i);
  }
  return bytes;
}

function utf8BytesToString(bytes) {
  if (typeof TextDecoder !== "undefined") {
    return new TextDecoder().decode(bytes);
  }
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  try {
    return decodeURIComponent(escape(binary));
  } catch (_) {
    return binary;
  }
}

export function payloadSnapshot(payload, label = "payload") {
  const maxBytes = Math.max(
    1024,
    Number.parseInt(process.env.SENTRY_MAX_EVENT_PAYLOAD_BYTES || "32768", 10) || 32768
  );
  const sanitized = scrubSensitiveData(payload);
  const raw = safeStringify(sanitized);
  const rawBytes = toUtf8Bytes(raw);
  const preview = utf8BytesToString(rawBytes.slice(0, maxBytes));

  return {
    [`${label}_hash`]: stringHash(raw),
    [`${label}_size_bytes`]: rawBytes.byteLength,
    [`${label}_truncated`]: rawBytes.byteLength > maxBytes,
    [`${label}_preview`]: preview,
  };
}

const CLIENT_CONFIG_TIMEOUT_MS = 5000;

/**
 * Sentry's settings for this pane. The bundle is built once and deployed to
 * every server, so the DSN and environment come from the backend the pane
 * talks to (GET /client-config, GID-83); a DSN baked into a local build wins.
 */
async function loadSentrySettings() {
  const builtDsn = (process.env.SENTRY_DSN || "").trim();
  if (builtDsn) {
    return {
      dsn: builtDsn,
      environment: process.env.SENTRY_ENVIRONMENT || "development",
      traces_sample_rate: coerceSampleRate(process.env.SENTRY_TRACES_SAMPLE_RATE, 0.15),
      replays_on_error_sample_rate: coerceSampleRate(process.env.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 1.0),
    };
  }
  if (typeof fetch !== "function") return null;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), CLIENT_CONFIG_TIMEOUT_MS) : null;
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/client-config`, {
      credentials: "omit",
      signal: controller?.signal,
    });
    if (!response.ok) return null;
    const body = await response.json();
    return body?.sentry?.dsn ? body.sentry : null;
  } catch (_) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Names the chat the user is in, and the trace of its newest reply, on every
 * pane event (GID-83), so a pane error can be read beside the backend's
 * Sentry event and the Langfuse trace for the same turn. Called with no
 * arguments to clear it (sign-out, account change).
 */
export function setSentryChatContext({ sessionId = null, traceId = null } = {}) {
  chatContext = {
    sessionId: sessionId ? String(sessionId) : null,
    traceId: traceId ? String(traceId) : null,
  };
}

export function getSentryChatContext() {
  return { ...chatContext };
}

// Tags an event already carries win: a stream report names its own turn.
export function withChatContext(event) {
  const tags = { ...(event?.tags || {}) };
  if (chatContext.sessionId && !tags.session_id) tags.session_id = chatContext.sessionId;
  if (chatContext.traceId && !tags.langfuse_trace_id) tags.langfuse_trace_id = chatContext.traceId;
  return { ...event, tags };
}

function applyPendingScope() {
  Object.entries(pendingTags).forEach(([key, value]) => Sentry.setTag(key, value));
  Object.entries(pendingContexts).forEach(([key, value]) => Sentry.setContext(key, value));
  pendingTags = {};
  pendingContexts = {};
  if (pendingUserId !== undefined) {
    const userId = pendingUserId;
    pendingUserId = undefined;
    setSentryUser(userId);
  }
}

/** Starts Sentry once; resolves to whether it is reporting. */
export function initSentry() {
  if (sentryInitialized) return sentryReady;
  sentryInitialized = true;
  sentryReady = loadSentrySettings().then((settings) => {
    if (!settings) {
      sentryEnabled = false;
      return false;
    }
    try {
      Sentry.init({
        dsn: settings.dsn,
        environment: settings.environment || "development",
        release: resolveRelease(),
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
          }),
        ],
        sendDefaultPii: false,
        tracesSampleRate: coerceSampleRate(settings.traces_sample_rate, 0.15),
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: coerceSampleRate(settings.replays_on_error_sample_rate, 1.0),
        tracePropagationTargets: resolveTracePropagationTargets(),
        beforeSend(event) {
          // Every user's errors, nobody's content (GID-83).
          const sanitized = redactEvent(scrubSensitiveData(withChatContext(event)));
          if (!sanitized.tags.layer) sanitized.tags.layer = "frontend";
          if (!sanitized.tags.host) sanitized.tags.host = "office_addin";
          return sanitized;
        },
        beforeBreadcrumb(breadcrumb) {
          return keepBreadcrumb(breadcrumb) ? scrubSensitiveData(breadcrumb) : null;
        },
      });
      Sentry.setTag("layer", "frontend");
      Sentry.setTag("host", "office_addin");
      sentryEnabled = true;
      applyPendingScope();
      return true;
    } catch (error) {
      console.warn("Sentry initialization failed:", error);
      sentryEnabled = false;
      return false;
    }
  });
  return sentryReady;
}

/**
 * The signed-in user, pseudonymous: the same hashed id the backend reports,
 * so one person's pane and server errors line up. Null clears it.
 */
// Bumped on every change, so a hash still in flight for a user who has since
// signed out cannot set them back.
let userGeneration = 0;

export function setSentryUser(userId) {
  userGeneration += 1;
  if (!sentryEnabled) {
    pendingUserId = userId || null;
    return;
  }
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  const generation = userGeneration;
  pseudonymousUserId(String(userId)).then((id) => {
    if (id && generation === userGeneration) Sentry.setUser({ id });
  });
}

export function isSentryEnabled() {
  return sentryEnabled;
}

export function setSentryScope(tags = {}, contexts = {}, user = null) {
  if (!sentryEnabled) {
    // Applied once Sentry has loaded its settings (see initSentry).
    Object.entries(tags || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim()) {
        pendingTags = { ...pendingTags, [key]: String(value) };
      }
    });
    Object.entries(contexts || {}).forEach(([key, value]) => {
      pendingContexts = { ...pendingContexts, [String(key)]: scrubSensitiveData(value) };
    });
    if (user?.id) pendingUserId = user.id;
    return;
  }
  if (tags) {
    Object.entries(tags).forEach(([key, value]) => {
      if (value !== null && value !== undefined && String(value).trim()) {
        Sentry.setTag(key, String(value));
      }
    });
  }
  if (contexts) {
    Object.entries(contexts).forEach(([key, value]) => {
      Sentry.setContext(String(key), scrubSensitiveData(value));
    });
  }
  if (user?.id) {
    setSentryUser(user.id);
  }
}

export function sentryAddBreadcrumb(breadcrumb = {}) {
  if (!sentryEnabled) return;
  Sentry.addBreadcrumb(scrubSensitiveData(breadcrumb));
}

export function sentryCaptureException(error, options = {}) {
  if (!sentryEnabled || !error) return;
  const errorValue = error instanceof Error ? error : new Error(String(error));
  Sentry.withScope((scope) => {
    setScopeValues(scope, options);
    Sentry.captureException(errorValue);
  });
}

export function sentryCaptureMessage(message, options = {}) {
  if (!sentryEnabled || !message) return;
  Sentry.withScope((scope) => {
    setScopeValues(scope, options);
    Sentry.captureMessage(String(message), options.level || "warning");
  });
}
